import { Temporal } from '@js-temporal/polyfill'
import type { UseQueryStatesKeysMap, Values } from 'nuqs'
import { createParser } from 'nuqs'
import { isUuid } from './zod'

export const parseAsPlainDate = createParser<Temporal.PlainDate>({
  parse: (value) => {
    try {
      return Temporal.PlainDate.from(value)
    } catch (error) {
      console.warn(`Error parsing date: ${value}`, error)
      return null
    }
  },
  serialize: (value) => {
    return value.toString()
  },
})

export const parseAsUuid = createParser({
  parse: (value) => {
    return isUuid(value) ? value : null
  },
  serialize: (value) => {
    return value
  },
})

interface DeserializeQueryParamsConfig<KeysMap extends UseQueryStatesKeysMap> {
  base: Values<KeysMap>
  queryParams: KeysMap
}

export function deserializeQueryParams<KeysMap extends UseQueryStatesKeysMap>(
  queryString: string,
  config: DeserializeQueryParamsConfig<KeysMap>,
) {
  const params = new URLSearchParams(queryString)
  const result = { ...config.base }

  for (const [key, parser] of Object.entries(config.queryParams)) {
    const value = params.get(key)
    if (value !== null) {
      // @ts-expect-error Object.entries loses the parser's input type.
      const parsedValue = parser.parse(value)
      if (parsedValue !== null) {
        // @ts-expect-error Object.entries loses the key-to-value relationship.
        result[key] = parsedValue
      }
    }
  }

  return result
}

export function pickQueryParamsByMap({
  queryParamMap,
  search,
}: {
  queryParamMap: UseQueryStatesKeysMap
  search: string
}) {
  const incomingParams = new URLSearchParams(search)
  const pickedParams = new URLSearchParams()
  const queryParamKeys = Object.keys(queryParamMap)
  const listQueryParamKeys = new Set(queryParamKeys)

  for (const [key, value] of incomingParams.entries()) {
    if (listQueryParamKeys.has(key)) {
      pickedParams.append(key, value)
    }
  }

  if (pickedParams.size === 0) {
    return ''
  }

  return `?${pickedParams.toString()}`
}
